'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { CacheKeys, ServerActionName } from '@headless-workspace/config';
import {
    AsyncData,
    ResultEmpty,
    ResultFailure,
    ResultSuccess,
    unwrapResultSuccess,
} from '@headless-workspace/core-domain';
import {
    getProductServerAction,
    mapProductDetailInformationValue,
    ProductDetailInformationValue,
    ProductDetailValue,
    ProductType,
    useSafeSWR,
} from '../../../../../../src';
import { ProductDetailsErrors } from '../ProductDetailsErrors';

type ProductDetailsInformationReturnType = {
    asyncData: AsyncData<ProductDetailInformationValue>;
    setSku: (sku: string) => void;
};

export const useProductDetailsInformation = (initialData: ProductDetailValue): ProductDetailsInformationReturnType => {
    const t = useTranslations('Discover.Pdp.productInfo.error');
    const [sku, setSku] = useState(
        initialData.type !== ProductType.PRODUCT_SET ? initialData.sku : initialData.productSetId,
    );

    const {
        data,
        error,
        isValidating,
        isLoading: swrIsLoading,
    } = useSafeSWR([CacheKeys.Products, sku], ServerActionName.getProductById, () => getProductServerAction(sku), {
        fallbackData: ResultSuccess(initialData),
        keepPreviousData: true,
        // /!\ Special case that does not use the defined RevalidationStrategy
        // In this case, we do not want to retrieve the data at mount because it has already been retrieved on the server side
        // However, we want to retrieve the data when ProductDetailProvider is updated, so we need to set revalidateIfStale to true to avoid blocking data retrieval
        revalidateOnMount: false,
        revalidateIfStale: true,
    });

    const isLoading = isValidating && swrIsLoading;

    const emptyResult = { asyncData: { isLoading, result: ResultEmpty() }, setSku };

    if (!data) {
        if (error) {
            return {
                asyncData: {
                    isLoading,
                    result: ResultFailure(
                        ProductDetailsErrors.Default,
                        error instanceof Error ? error.message : t('default'),
                    ),
                },
                setSku,
            };
        }
        return emptyResult;
    }

    const productDetailValue = unwrapResultSuccess(data);

    if (!productDetailValue) {
        return emptyResult;
    }

    return {
        asyncData: {
            isLoading,
            result: ResultSuccess(mapProductDetailInformationValue(productDetailValue)),
        },
        setSku,
    };
};
